import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Sparkles, ArrowRightCircle, Lightbulb, Archive, Loader2,
} from 'lucide-react';
import { ideasApi } from '../api/services';
import type { Idea, IdeaStatus } from '../types/models';
import DictationTextField from '../components/DictationTextField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// IdeasPage — capture-then-assess flow. Stats strip at the top, then three
// tabs (Active / Converted / Archived), then a list of idea cards with
// inline actions (Convert / Archive / Delete) and a click-through detail
// dialog where the AI assessment is run / re-run.
//
// One MUI hold-out: DictationTextField in the capture dialog (same speech-
// to-text wrapper used by ReflectionsPage). Tracked in BACKLOG.
// ---------------------------------------------------------------------------

const STATUS_CHIP: Record<IdeaStatus, string> = {
  Captured: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  Assessed: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  Converted: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  Archived: 'bg-muted text-muted-foreground',
};

type TabFilter = 'Active' | 'Converted' | 'Archived';

export default function IdeasPage() {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabFilter, setTabFilter] = useState<TabFilter>('Active');

  // Capture dialog
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureTitle, setCaptureTitle] = useState('');
  const [captureContent, setCaptureContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Selected idea detail
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [assessError, setAssessError] = useState<string | null>(null);

  // Convert dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertTitle, setConvertTitle] = useState('');
  const [convertPriority, setConvertPriority] = useState('Medium');
  const [convertMinutes, setConvertMinutes] = useState('30');
  const [converting, setConverting] = useState(false);

  const loadIdeas = useCallback(async () => {
    setLoading(true);
    try {
      setIdeas(await ideasApi.getAll());
    } catch (err) {
      console.error('Failed to load ideas', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const handleCapture = async () => {
    if (!captureTitle.trim()) return;
    setSaving(true);
    try {
      const idea = await ideasApi.create({ title: captureTitle.trim(), content: captureContent.trim() || undefined });
      setIdeas(prev => [idea, ...prev]);
      setCaptureOpen(false);
      setCaptureTitle('');
      setCaptureContent('');
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      await ideasApi.delete(id);
      setIdeas(prev => prev.filter(i => i.id !== id));
      if (selectedIdea?.id === id) setDetailOpen(false);
    } catch { /* ignore */ }
  };

  const handleArchive = async (idea: Idea) => {
    try {
      const updated = await ideasApi.update(idea.id, { status: 'Archived' });
      setIdeas(prev => prev.map(i => i.id === idea.id ? updated : i));
      if (selectedIdea?.id === idea.id) setSelectedIdea(updated);
    } catch { /* ignore */ }
  };

  const handleAssess = async () => {
    if (!selectedIdea) return;
    setAssessing(true);
    setAssessError(null);
    try {
      const updated = await ideasApi.assess(selectedIdea.id);
      setIdeas(prev => prev.map(i => i.id === updated.id ? updated : i));
      setSelectedIdea(updated);
    } catch {
      setAssessError('AI assessment failed. Please try again.');
    } finally {
      setAssessing(false);
    }
  };

  const openConvert = (idea: Idea) => {
    setSelectedIdea(idea);
    setConvertTitle(idea.title);
    setConvertPriority('Medium');
    setConvertMinutes('30');
    setConvertOpen(true);
  };

  const handleConvert = async () => {
    if (!selectedIdea) return;
    setConverting(true);
    try {
      await ideasApi.convert(selectedIdea.id, {
        title: convertTitle,
        priority: convertPriority,
        estimatedMinutes: parseInt(convertMinutes) || 30,
      });
      const updated = await ideasApi.getById(selectedIdea.id);
      setIdeas(prev => prev.map(i => i.id === updated.id ? updated : i));
      setSelectedIdea(updated);
      setConvertOpen(false);
      setDetailOpen(false);
    } catch { /* ignore */ }
    finally { setConverting(false); }
  };

  const openDetail = (idea: Idea) => {
    setSelectedIdea(idea);
    setAssessError(null);
    setDetailOpen(true);
  };

  const filteredIdeas = ideas.filter(i => {
    if (tabFilter === 'Active') return i.status === 'Captured' || i.status === 'Assessed';
    if (tabFilter === 'Converted') return i.status === 'Converted';
    return i.status === 'Archived';
  });

  const activeCount = ideas.filter(i => i.status === 'Captured' || i.status === 'Assessed').length;

  return (
    <div className="mx-auto max-w-[1100px] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ideas</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Capture thoughts, assess strategic alignment, convert to work items
          </p>
        </div>
        <Button onClick={() => setCaptureOpen(true)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Capture Idea
        </Button>
      </div>

      {/* Stats */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-center gap-6 py-4">
          <div>
            <p className="text-xs text-muted-foreground">Active Ideas</p>
            <p className="text-2xl font-semibold tabular-nums">{activeCount}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Assessed</p>
            <p className="text-2xl font-semibold tabular-nums">
              {ideas.filter(i => i.status === 'Assessed').length}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Converted to Work Items</p>
            <p className="text-2xl font-semibold tabular-nums">
              {ideas.filter(i => i.status === 'Converted').length}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs
        value={tabFilter}
        onValueChange={(v) => setTabFilter(v as TabFilter)}
        className="mb-4"
      >
        <TabsList>
          <TabsTrigger value="Active">Active ({activeCount})</TabsTrigger>
          <TabsTrigger value="Converted">Converted</TabsTrigger>
          <TabsTrigger value="Archived">Archived</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && filteredIdeas.length === 0 && (
        <div className="py-12 text-center">
          <Lightbulb className="mx-auto mb-2 h-12 w-12 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {tabFilter === 'Active'
              ? 'No ideas yet — capture your first one'
              : `No ${tabFilter.toLowerCase()} ideas`}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {filteredIdeas.map(idea => (
          <button
            key={idea.id}
            type="button"
            onClick={() => openDetail(idea)}
            className="text-left transition-colors"
          >
            <Card className="border-border transition-colors hover:border-primary/60 hover:bg-primary/5">
              <CardContent className="py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-medium">{idea.title}</p>
                    {idea.content && (
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">
                        {idea.content}
                      </p>
                    )}
                    {idea.aiAlignmentAssessment && (
                      <p className="mt-1 text-xs text-primary">
                        {idea.aiAlignmentAssessment.substring(0, 120)}…
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                          STATUS_CHIP[idea.status],
                        )}
                      >
                        {idea.status}
                      </span>
                      {idea.suggestedDriver && (
                        <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                          {idea.suggestedDriver.title}
                        </span>
                      )}
                      {idea.suggestedProject && (
                        <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                          {idea.suggestedProject.title}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(idea.status === 'Captured' || idea.status === 'Assessed') && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => openConvert(idea)}>
                            <ArrowRightCircle className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Convert to work item</TooltipContent>
                      </Tooltip>
                    )}
                    {idea.status !== 'Archived' && idea.status !== 'Converted' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleArchive(idea)}>
                            <Archive className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Archive</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="icon-sm" variant="ghost" onClick={() => handleDelete(idea.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {/* Capture dialog */}
      <Dialog open={captureOpen} onOpenChange={(o) => !o && setCaptureOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Capture Idea</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="idea-title">Title</Label>
              <Input
                id="idea-title"
                value={captureTitle}
                onChange={(e) => setCaptureTitle(e.target.value)}
                placeholder="What's the idea?"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCapture(); }
                }}
              />
            </div>
            {/* DictationTextField is the MUI hold-out — owns the speech-to-
                text mic adornment + pulse animation. Tracked in BACKLOG. */}
            <DictationTextField
              label="Detail (optional)"
              value={captureContent}
              onChange={(e) => setCaptureContent(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Add context, motivation, or initial thinking… (mic icon dictates)"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCaptureOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCapture}
              disabled={saving || !captureTitle.trim()}
              className="gap-1.5"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : 'Capture'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Idea detail / assess dialog */}
      <Dialog open={detailOpen} onOpenChange={(o) => !o && setDetailOpen(false)}>
        <DialogContent className="max-w-lg">
          {selectedIdea && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-2">
                  <DialogTitle className="flex-1">{selectedIdea.title}</DialogTitle>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                      STATUS_CHIP[selectedIdea.status],
                    )}
                  >
                    {selectedIdea.status}
                  </span>
                </div>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                {selectedIdea.content && (
                  <p className="text-sm text-muted-foreground">{selectedIdea.content}</p>
                )}
                <hr className="border-border" />
                <div>
                  <p className="mb-2 text-sm font-semibold">AI Alignment Assessment</p>
                  {selectedIdea.aiAlignmentAssessment ? (
                    <Alert variant="info">
                      <AlertDescription className="flex items-start gap-2">
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{selectedIdea.aiAlignmentAssessment}</span>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <p className="mb-2 text-sm text-muted-foreground/60">
                      No assessment yet. Run AI assessment to see how this idea aligns with your strategic drivers.
                    </p>
                  )}
                  {selectedIdea.suggestedDriver && (
                    <div className="mt-2 flex items-center gap-2">
                      <p className="text-xs text-muted-foreground">Suggested driver:</p>
                      <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                        {selectedIdea.suggestedDriver.title}
                      </span>
                    </div>
                  )}
                  {selectedIdea.suggestedProject && (
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-xs text-muted-foreground">Suggested project:</p>
                      <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                        {selectedIdea.suggestedProject.title}
                      </span>
                    </div>
                  )}
                </div>
                {assessError && (
                  <Alert variant="error">
                    <AlertDescription>{assessError}</AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDetailOpen(false)}>Close</Button>
                {selectedIdea.status !== 'Converted' && selectedIdea.status !== 'Archived' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleAssess}
                      disabled={assessing}
                      className="gap-1.5"
                    >
                      {assessing
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Sparkles className="h-4 w-4" />}
                      {selectedIdea.aiAlignmentAssessment ? 'Re-assess' : 'Assess with AI'}
                    </Button>
                    <Button
                      onClick={() => { setDetailOpen(false); openConvert(selectedIdea); }}
                      className="gap-1.5"
                    >
                      <ArrowRightCircle className="h-4 w-4" />
                      Convert to Work Item
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={convertOpen} onOpenChange={(o) => !o && setConvertOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Convert to Work Item</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-title">Title</Label>
              <Input
                id="convert-title"
                value={convertTitle}
                onChange={(e) => setConvertTitle(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-priority">Priority</Label>
              <Select value={convertPriority} onValueChange={setConvertPriority}>
                <SelectTrigger id="convert-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Critical', 'High', 'Medium', 'Low'].map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-minutes">Estimated minutes</Label>
              <Input
                id="convert-minutes"
                type="number"
                value={convertMinutes}
                onChange={(e) => setConvertMinutes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConvertOpen(false)}>Cancel</Button>
            <Button onClick={handleConvert} disabled={converting} className="gap-1.5">
              {converting && <Loader2 className="h-4 w-4 animate-spin" />}
              {converting ? 'Converting…' : 'Convert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
